import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdForgeLogo from '../../components/feature/AdForgeLogo';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setError('');
    setIsLoading(true);
    const { error: authError } = await resetPassword(email);
    setIsLoading(false);
    if (authError) {
      setError(authError);
    } else {
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans flex">
      <div className="hidden lg:flex lg:w-1/2 bg-stone-950 flex-col justify-between p-12">
        <Link to="/">
          <AdForgeLogo size="md" light={true} />
        </Link>
        <div>
          <h2 className="text-3xl font-extrabold text-white mb-4 leading-snug">
            Locked out? <br />No worries, we&apos;ve got you.
          </h2>
          <p className="text-stone-400 text-sm leading-relaxed max-w-xs">
            Enter your email and we&apos;ll send a secure link to reset your password. It only takes a moment.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className="ri-star-fill text-amber-400 text-sm" />
            ))}
          </div>
          <span className="text-sm text-stone-400">Rated 4.9/5 by 3,200+ businesses</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Link to="/">
              <AdForgeLogo size="lg" light={false} />
            </Link>
          </div>

          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-5">
                <i className="ri-mail-send-line text-3xl text-orange-500" />
              </div>
              <h1 className="text-2xl font-extrabold text-stone-900 mb-2">Check your inbox</h1>
              <p className="text-sm text-stone-500 mb-6 leading-relaxed">
                We&apos;ve sent a password reset link to <strong className="text-stone-700">{email}</strong>.
                Check your email and click the link to set a new password.
              </p>
              <p className="text-xs text-stone-400 mb-6">
                Didn&apos;t get it? Check your spam folder or{' '}
                <button
                  type="button"
                  onClick={() => { setSent(false); }}
                  className="text-orange-500 font-semibold hover:underline cursor-pointer"
                >
                  try again
                </button>.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors"
              >
                <i className="ri-arrow-left-line" />
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 mb-6 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                Back to sign in
              </Link>

              <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Forgot your password?</h1>
              <p className="text-sm text-stone-500 mb-8">
                Enter your email and we&apos;ll send you a reset link.
              </p>

              {error && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">Email address</label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70"
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Sending...</>
                  ) : (
                    <><i className="ri-mail-send-line" /> Send Reset Link</>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
